'use client';

import { useEffect, useRef } from 'react';

export default function WaveCanvas({ className = '' }: { className?: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const raf = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let w = 0, h = 0, t = 0;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      t += 0.008;

      const waves = [
        { amp: 25, freq: 0.008, speed: 1, alpha: 0.08, yOff: 0.3 },
        { amp: 18, freq: 0.012, speed: 1.4, alpha: 0.06, yOff: 0.5 },
        { amp: 30, freq: 0.006, speed: 0.7, alpha: 0.04, yOff: 0.7 },
      ];

      for (const wave of waves) {
        const baseY = h * wave.yOff;
        ctx.beginPath();
        ctx.moveTo(0, h);

        for (let x = 0; x <= w; x += 2) {
          const y = baseY + Math.sin(x * wave.freq + t * wave.speed) * wave.amp
                         + Math.sin(x * wave.freq * 0.5 + t * wave.speed * 0.8) * wave.amp * 0.5;
          ctx.lineTo(x, y);
        }

        ctx.lineTo(w, h);
        ctx.closePath();

        const grad = ctx.createLinearGradient(0, baseY - wave.amp, 0, h);
        grad.addColorStop(0, `rgba(200, 169, 126, ${wave.alpha})`);
        grad.addColorStop(1, `rgba(200, 169, 126, 0)`);
        ctx.fillStyle = grad;
        ctx.fill();
      }

      raf.current = requestAnimationFrame(draw);
    };

    resize();
    draw();

    window.addEventListener('resize', resize);
    return () => {
      cancelAnimationFrame(raf.current);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas data-component-id="src/components/effects/WaveCanvas.tsx:72:4" data-component-path="src/components/effects/WaveCanvas.tsx" data-component-line="72" data-component-end-line="75" data-component-file="WaveCanvas.tsx" data-component-name="canvas" data-component-content="%7B%22elementName%22%3A%22canvas%22%7D"
      ref={canvasRef}
      className={`absolute inset-0 w-full h-full pointer-events-none ${className}`}
    />
  );
}
